import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faShareAlt,
  faTimes,
  faStar,
  faAward,
  faCalendarAlt,
  faClock,
  faCheckCircle,
  faExternalLinkAlt,
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence, color } from "framer-motion";
import ApiServices from "../Services/ApiServices";
import { decryptId } from "../../utils/encryption";
import { getFullFileUrl } from "../../Helpers/UrlHelpers";
import { storageHelper } from "../../Helpers/storageHelper";
import MiniLoader from "./MiniLoader";

const CertificateViewer = ({
  courseId,
  onClose,
  showControls = true,
  className = "",
  style = {},
}) => {
  const { id: encryptedId } = useParams();
  const [certificateData, setCertificateData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [decryptedCourseId, setDecryptedCourseId] = useState(null);
  const [userImage, setUserImage] = useState(null);
  const [activeTab, setActiveTab] = useState("details"); // 'details' or 'info'
  const certificateRef = useRef(null);
  const fetchedIdRef = useRef(null);

  const fetchCertificateData = async (targetId, force = false) => {
    const idToUse = (typeof targetId === "string" ? targetId : null) || courseId || encryptedId;
    if (!idToUse) return;

    if (!force && fetchedIdRef.current === idToUse) {
      return;
    }

    fetchedIdRef.current = idToUse;

    try {
      setLoading(true);
      setError(null);

      const response = await ApiServices.get(
        `MyCourse/GetCourseCertificate/${idToUse}`
      );
      setCertificateData(response.data);
      return response.data;
    } catch (error) {
      fetchedIdRef.current = null;
      setError("Failed to load certificate data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // If courseId is provided as prop, use it directly
    if (courseId) {
      setDecryptedCourseId(courseId);
      fetchCertificateData(courseId);
      return;
    }

    // Otherwise, decrypt the ID from URL parameter
    if (encryptedId) {
      const decrypted = decryptId(encryptedId);
      if (decrypted) {
        setDecryptedCourseId(decrypted);
        fetchCertificateData(encryptedId);
      } else {
        setError("Invalid certificate link");
        setLoading(false);
      }
    }
  }, [courseId, encryptedId]);

  useEffect(() => {
    // Get user image from storage
    const storedUserImage = storageHelper.getItem("user_image");
    if (storedUserImage) {
      setUserImage(getFullFileUrl(storedUserImage));
    }
  }, []);

  const handleDownload = () => {
    if (certificateData?.certificate) {
      const link = document.createElement("a");
      const idToUse = courseId ? courseId : encryptedId;
      link.download = `certificate-${idToUse}.png`;
      link.href = `data:image/png;base64,${certificateData.certificate}`;
      link.click();
    }
  };

  const handleShare = async () => {
    if (certificateData?.certificate) {
      try {
        const base64Data = certificateData.certificate;
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'image/png' });

        const idToUse = courseId ? courseId : encryptedId;
        const file = new File([blob], `certificate-${idToUse}.png`, { type: 'image/png' });

        if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: "My Certificate",
            text: `I just earned a certificate in "${certificateData.course_name}"! Check it out.`,
            files: [file],
          });
        } else {
          const url = URL.createObjectURL(blob);
          if (navigator.clipboard && navigator.clipboard.write) {
            const clipboardItem = new ClipboardItem({ 'image/png': blob });
            await navigator.clipboard.write([clipboardItem]);
            alert("Certificate image copied to clipboard!");
          } else {
            const link = document.createElement("a");
            link.href = url;
            link.download = `certificate-${idToUse}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            alert("Certificate image has been downloaded!");
          }
        }
      } catch (error) {

        alert("Unable to share certificate image. Please try downloading instead.");
      }
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    const filledStars = Math.floor(rating || 0);
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <FontAwesomeIcon
          key={i}
          icon={faStar}
          className={i <= filledStars ? "star-icon filled" : "star-icon"}
        />
      );
    }
    return stars;
  };



  if (error) {
    return (
      <div className="error-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="error-content"
        >
          <div className="error-icon-wrapper">
            <FontAwesomeIcon icon={faTimes} />
          </div>
          <h3>Oops! Something went wrong</h3>
          <p>{error}</p>
          <button onClick={() => fetchCertificateData(null, true)}>Try Again</button>
        </motion.div>
      </div>
    );
  }

  if (!decryptedCourseId) return null;

  const hasData = !!certificateData;

  return (
    <div className={`modern-certificate-viewer ${className}`} style={style} dir="rtl">
      <AnimatePresence mode="wait">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="viewer-overlay"
        >
          {/* Top Navbar */}
          <nav className="viewer-nav">
            <div className="nav-brand">
              <span className="logo-accent">E</span>MT7ANY
            </div>
            <div className="nav-actions">
              {showControls && (
                <>
                  <button className="nav-btn share" onClick={handleShare}>
                    <FontAwesomeIcon icon={faShareAlt} />
                    <span>مشاركة</span>
                  </button>
                  <button className="nav-btn download" onClick={handleDownload}>
                    <FontAwesomeIcon icon={faDownload} />
                    <span>تحميل</span>
                  </button>
                </>
              )}
              {onClose && (
                <button className="close-btn-modern" onClick={onClose} aria-label="Close">
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
          </nav>

          <main className="viewer-content">
            {/* Left Section: Certificate Preview */}
            <section className="certificate-display-section">
              <motion.div
                initial={{ scale: 0.9, opacity: 0, rotateY: 5 }}
                animate={{ scale: 1, opacity: 1, rotateY: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="certificate-card-wrapper"
              >
                <div className="certificate-glow"></div>
                {loading && <MiniLoader overlay={true} size={40} />}
                {certificateData?.certificate ? (
                  <div className="image-container-modern">
                    <img
                      src={`data:image/png;base64,${certificateData.certificate}`}
                      alt="Course Certificate"
                      className="main-certificate-img"
                    />
                    <div className="verification-badge">
                      <FontAwesomeIcon icon={faCheckCircle} />
                      موثق بواسطة امتحاني
                    </div>
                  </div>
                ) : (
                  <div className="certificate-fallback-modern">
                    <div className="fallback-content">
                      <FontAwesomeIcon icon={faAward} className="fallback-icon" />
                      <h3>{hasData ? "الشهادة جاهزة" : "لم يتم العثور على الشهادة"}</h3>
                      <p>{hasData ? "جاري إنشاء معاينة عالية الجودة..." : "تعذر تحميل الشهادة. يرجى التأكد من إكمال جميع متطلبات الدورة."}</p>
                    </div>
                  </div>
                )}
              </motion.div>
            </section>

            {/* Right Section: Details & Sidebar */}
            <aside className="details-sidebar-modern">
              <motion.div
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="sidebar-glass-card"
              >
                <div className="sidebar-header">
                  <h3>تفاصيل الشهادة</h3>
                  <div className="divider"></div>
                </div>

                <div className="recipient-profile-card">
                  <div className="avatar-wrapper">
                    <img
                      src={userImage || "/assets/images/owner.png"}
                      alt="Recipient"
                      onError={(e) => {
                        e.target.src = "https://ui-avatars.com/api/?name=" + (certificateData?.student_name || "User") + "&background=A435F0&color=fff";
                      }}
                    />
                  </div>
                  <div className="recipient-info-text">
                    <span className="info-label">مستلم الشهادة</span>
                    <h4 className="info-value">{certificateData?.student_name || "اسم الطالب"}</h4>
                  </div>
                </div>

                <div className="course-info-card-modern">
                  <div className="course-banner">
                    <img
                      src={getFullFileUrl(certificateData?.course_about?.course_cover)}
                      alt={certificateData?.course_about?.course_name}
                      onError={(e) => { e.target.src = "/assets/images/new_log_design.svg"; }}
                    />
                    <div className="course-tag">دورة تدريبية</div>
                  </div>
                  <div className="course-details-body">
                    <h2 className="course-name-modern">
                      {certificateData?.course_about?.course_name || certificateData?.course_name || "اسم الدورة"}
                    </h2>
                    <p className="instructor-modern">
                      <span>بواسطة</span> {certificateData?.course_about?.instructor_name || certificateData?.instructor_name || "المحاضر"}
                    </p>

                    <div className="rating-row-modern">
                      <div className="stars-wrapper">
                        {renderStars(certificateData?.course_about?.course_rating || 4.2)}
                      </div>
                      <span className="rating-text">
                        {(certificateData?.course_about?.course_rating || 4.2).toFixed(1)}
                      </span>
                    </div>

                    <div className="stats-grid-modern">
                      <div className="stat-item">
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        <div className="stat-text">
                          <label>تاريخ الإكمال</label>
                          <span>
                            {certificateData?.finish_date && certificateData?.finish_date !== "0001-01-01T00:00:00"
                              ? new Date(certificateData.finish_date).toLocaleDateString("ar-EG", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })
                              : "غير متوفر"}
                          </span>
                        </div>
                      </div>
                      <div className="stat-item">
                        <FontAwesomeIcon icon={faClock} />
                        <div className="stat-text">
                          <label>المدة</label>
                          <span>{certificateData?.course_about?.video_count || "--"} محاضرة</span>
                        </div>
                      </div>
                    </div>

                    <div className="certificate-meta-box">
                      <div className="meta-item">
                        <label>رقم الشهادة</label>
                        <code>EM-{String(decryptedCourseId || "").substring(0, 8) || "CERT-IDX"}</code>
                      </div>
                      <div className="meta-item">
                        <label>الحالة</label>
                        <span className="status-badge">موثقة</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </aside>
          </main>
        </motion.div>
      </AnimatePresence>

      <style jsx>{`
        .modern-certificate-viewer {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          height: 100dvh;
          z-index: 9999;
          font-family: "Zain", sans-serif;
          color: #fff;
        }

        .viewer-overlay {
          width: 100%;
          height: 100%;
          background: radial-gradient(circle at 50% 50%, #1a1b1e 0%, #0a0a0b 100%);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        /* Nav Styles */
        .viewer-nav {
          height: 70px;
          padding: 0 40px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(15px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
          z-index: 10;
        }

        .nav-brand {
          font-size: 20px;
          font-weight: 800;
          letter-spacing: 2px;
          color: #fff;
        }

        .logo-accent {
          color: #A435F0;
        }

        .nav-actions {
          display: flex;
          gap: 15px;
          align-items: center;
        }

        .nav-btn {
          padding: 10px 18px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .nav-btn.share {
          background: rgba(255, 255, 255, 0.05);
          color: #fff;
        }

        .nav-btn.share:hover {
          background: rgba(255, 255, 255, 0.12);
          transform: translateY(-2px);
          border-color: rgba(255, 255, 255, 0.2);
        }

        .nav-btn.download {
          background: linear-gradient(135deg, #A435F0 0%, #7c26b5 100%);
          color: #fff;
          border: none;
          box-shadow: 0 4px 15px rgba(164, 53, 240, 0.3);
        }

        .nav-btn.download:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(164, 53, 240, 0.5);
          filter: brightness(1.1);
        }

        .close-btn-modern {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          border: none;
          background: rgba(255, 255, 255, 0.08);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .close-btn-modern:hover {
          background: #ef4444;
          transform: rotate(90deg) scale(1.1);
        }

        /* Main Content */
        .viewer-content {
          flex: 1;
          display: flex;
          padding: 40px;
          gap: 40px;
          overflow: hidden;
        }

        .certificate-display-section {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .certificate-card-wrapper {
          position: relative;
          max-width: 900px;
          width: 100%;
          perspective: 2000px;
        }

        .certificate-glow {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 120%;
          height: 120%;
          background: radial-gradient(circle, rgba(164, 53, 240, 0.15) 0%, transparent 70%);
          filter: blur(60px);
          z-index: -1;
        }

        .image-container-modern {
          position: relative;
          background: #fff;
          padding: 8px;
          border-radius: 12px;
          box-shadow: 0 30px 60px -12px rgba(0, 0, 0, 0.6);
          overflow: hidden;
          transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .image-container-modern:hover {
          transform: rotateX(2deg) rotateY(-2deg) scale(1.02);
        }

        .main-certificate-img {
          width: 100%;
          height: auto;
          display: block;
          border-radius: 6px;
        }

        .verification-badge {
          position: absolute;
          bottom: 25px;
          right: 25px;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(5px);
          color: #059669;
          padding: 10px 18px;
          border-radius: 50px;
          font-size: 13px;
          font-weight: 800;
          display: flex;
          align-items: center;
          gap: 8px;
          box-shadow: 0 10px 20px rgba(0,0,0,0.15);
          border: 1px solid rgba(5, 150, 105, 0.1);
        }

        .certificate-fallback-modern {
          width: 100%;
          aspect-ratio: 1.414;
          background: rgba(255, 255, 255, 0.02);
          border: 2px dashed rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
        }
         .fallback-content h3{
          color: #fff;
         }
         .fallback-content p{
          color: #fff;
         }
        .fallback-icon {
          font-size: 80px;
          color: #A435F0;
          margin-bottom: 24px;
          opacity: 0.4;
        }

        /* Sidebar Styles */
        .details-sidebar-modern {
          width: 440px;
          height: 100%;
          overflow: visible;
        }

        .sidebar-glass-card {
          background: rgba(255, 255, 255, 0.04);
          backdrop-filter: blur(30px);
          border-radius: 24px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          padding: 35px;
          display: flex;
          flex-direction: column;
          gap: 25px;
          height: fit-content;
          max-height: calc(100vh - 150px);
          overflow-y: auto;
          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none; /* IE and Edge */
        }

        .sidebar-glass-card::-webkit-scrollbar {
          display: none; /* Chrome, Safari, Opera */
        }

        .sidebar-header h3 {
          font-size: 20px;
          font-weight: 800;
          margin-bottom: 12px;
          color: rgba(255, 255, 255, 0.95);
        }

        .divider {
          height: 3px;
          width: 50px;
          background: linear-gradient(90deg, #A435F0, #8b2ec9);
          border-radius: 4px;
        }

        .recipient-profile-card {
          display: flex;
          align-items: center;
          gap: 18px;
          padding: 18px;
          background: rgba(255, 255, 255, 0.03);
          border-radius: 16px;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .avatar-wrapper {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          overflow: hidden;
          border: 2px solid #A435F0;
          flex-shrink: 0;
          box-shadow: 0 0 15px rgba(164, 53, 240, 0.3);
        }

        .avatar-wrapper img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .recipient-info-text {
          display: flex;
          flex-direction: column;
        }

        .info-label {
          font-size: 11px;
          text-transform: uppercase;
          color: rgba(255, 255, 255, 0.4);
          letter-spacing: 1.5px;
          margin-bottom: 6px;
          font-weight: 700;
        }

        .info-value {
          font-size: 20px;
          font-weight: 800;
          margin: 0;
          color: #fff;
        }

        .course-info-card-modern {
          background: rgba(255, 255, 255, 0.03);
          border-radius: 20px;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.05);
          transition: all 0.3s ease;
        }

        .course-info-card-modern:hover {
          background: rgba(255, 255, 255, 0.05);
          border-color: rgba(255, 255, 255, 0.1);
        }

        .course-banner {
          position: relative;
          height: 160px;
        }

        .course-banner img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .course-tag {
          position: absolute;
          top: 20px;
          left: 20px;
          background: #A435F0;
          color: #fff;
          padding: 6px 14px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 900;
          letter-spacing: 1.2px;
          box-shadow: 0 4px 10px rgba(0,0,0,0.3);
        }

        .course-details-body {
          padding: 24px;
        }

        .course-name-modern {
          font-size: 22px;
          font-weight: 800;
          line-height: 1.4;
          margin-bottom: 10px;
          color: #fff;
        }

        .instructor-modern {
          font-size: 15px;
          margin-bottom: 20px;
          color: rgba(255, 255, 255, 0.7);
        }

        .instructor-modern span {
          opacity: 0.5;
          margin-right: 6px;
        }

        .rating-row-modern {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 24px;
        }

        .star-icon {
          color: rgba(255, 255, 255, 0.15);
          font-size: 13px;
          margin-right: 3px;
        }

        .star-icon.filled {
          color: #fbbf24;
        }

        .rating-text {
          font-weight: 800;
          color: #fbbf24;
          font-size: 16px;
        }

        .stats-grid-modern {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          padding: 20px 0;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          margin-bottom: 24px;
        }

        .stat-item {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .stat-item :global(svg) {
          color: #A435F0;
          font-size: 16px;
          opacity: 0.8;
        }

        .stat-text label {
          display: block;
          font-size: 10px;
          color: rgba(255, 255, 255, 0.4);
          text-transform: uppercase;
          margin-bottom: 4px;
          font-weight: 700;
          letter-spacing: 0.5px;
        }

        .stat-text span {
          display: block;
          font-size: 14px;
          font-weight: 700;
          color: rgba(255, 255, 255, 0.95);
        }

        .certificate-meta-box {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }

        .meta-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .meta-item label {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.5);
          font-weight: 500;
        }

        .meta-item code {
          background: rgba(0, 0, 0, 0.4);
          padding: 5px 10px;
          border-radius: 6px;
          font-size: 12px;
          color: #A435F0;
          font-family: 'JetBrains Mono', monospace;
        }

        .status-badge {
          font-size: 12px;
          color: #10b981;
          font-weight: 800;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .status-badge::before {
          content: "";
          width: 6px;
          height: 6px;
          background: #10b981;
          border-radius: 50%;
          box-shadow: 0 0 8px #10b981;
        }

        .sidebar-footer-actions {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .primary-action-btn {
          width: 100%;
          padding: 16px;
          border-radius: 14px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          background: rgba(255, 255, 255, 0.06);
          color: #fff;
          font-weight: 800;
          font-size: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          cursor: pointer;
          transition: all 0.3s;
        }

        .primary-action-btn:hover {
          background: rgba(255, 255, 255, 0.12);
          border-color: rgba(255, 255, 255, 0.2);
          transform: translateY(-3px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.2);
        }

        .update-hint-modern {
          text-align: center;
          font-size: 13px;
          color: rgba(255, 255, 255, 0.5);
        }

        .update-hint-modern span {
          color: #A435F0;
          font-weight: 700;
          cursor: pointer;
          margin-left: 4px;
          transition: color 0.2s;
        }

        .update-hint-modern span:hover {
          color: #8b2ec9;
          text-decoration: underline;
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .details-sidebar-modern {
            width: 380px;
          }
        }

        @media (max-width: 992px) {
          .viewer-overlay {
            height: 100%;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
          }

          .viewer-content {
            flex-direction: column;
            padding: 24px 20px;
            gap: 24px;
            overflow: visible;
            height: auto;
            flex: none;
          }

          .details-sidebar-modern {
            width: 100%;
            height: auto;
          }

          .sidebar-glass-card {
            max-height: none;
            overflow-y: visible;
          }

          .certificate-card-wrapper {
            max-width: 100%;
          }
        }

        @media (max-width: 768px) {
          .viewer-nav {
            height: 60px;
            padding: 0 16px;
          }

          .nav-brand {
            font-size: 17px;
            letter-spacing: 1px;
          }

          .nav-actions {
            gap: 8px;
          }

          .nav-btn {
            padding: 8px 14px;
            font-size: 13px;
            border-radius: 8px;
          }

          .nav-btn span {
            display: inline-block;
          }

          .viewer-content {
            padding: 16px 12px;
            gap: 20px;
          }

          .sidebar-glass-card {
            padding: 20px 16px;
            border-radius: 18px;
            gap: 18px;
          }

          .recipient-profile-card {
            padding: 14px;
            gap: 12px;
          }

          .avatar-wrapper {
            width: 48px;
            height: 48px;
          }

          .info-value {
            font-size: 16px;
          }

          .course-name-modern {
            font-size: 18px;
          }

          .course-banner {
            height: 130px;
          }

          .course-details-body {
            padding: 16px;
          }

          .verification-badge {
            bottom: 12px;
            right: 12px;
            padding: 6px 12px;
            font-size: 11px;
          }

          .certificate-glow {
            width: 100%;
            height: 100%;
            filter: blur(30px);
          }
        }

        @media (max-width: 576px) {
          .viewer-nav {
            padding: 0 12px;
            height: 56px;
          }

          .nav-brand {
            font-size: 15px;
          }

          .nav-btn {
            padding: 6px 10px;
            font-size: 12px;
            gap: 6px;
          }

          .close-btn-modern {
            width: 32px;
            height: 32px;
            font-size: 13px;
          }

          .stats-grid-modern {
            grid-template-columns: 1fr;
            gap: 12px;
            padding: 14px 0;
          }

          .verification-badge {
            bottom: 8px;
            right: 8px;
            padding: 4px 8px;
            font-size: 10px;
            gap: 4px;
          }

          .recipient-info-text .info-label {
            font-size: 10px;
          }

          .recipient-info-text .info-value {
            font-size: 15px;
          }

          .course-name-modern {
            font-size: 16px;
          }

          .instructor-modern {
            font-size: 13px;
            margin-bottom: 14px;
          }
        }

        @media (max-width: 380px) {
          .nav-btn span {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default CertificateViewer;
